import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { brandColors } from '../../../../theme/theme';

/**
 * DemographicPerformanceDashboard Component
 * 
 * Interactive grid with all 7 metrics
 * Sortable columns, color gradients for quick scanning
 * Click to drill down (future enhancement)
 */
const DemographicPerformanceDashboard = ({ data }) => {
  const [orderBy, setOrderBy] = useState('totalResponses');
  const [order, setOrder] = useState('desc');
  const [selectedMetric, setSelectedMetric] = useState('all');

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for demographic performance dashboard
        </Typography>
      </Paper>
    );
  }

  const getFieldValue = (record, field) => {
    if (!record) return 0;

    const resolvedField = field.replace(/[^a-zA-Z0-9]/g, '');

    const variants = [
      field,
      field.charAt(0).toLowerCase() + field.slice(1),
      field.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, ''),
      field.replace(/([A-Z])/g, '_$1').toUpperCase().replace(/^_/, ''),
      resolvedField,
      resolvedField.toLowerCase(),
      resolvedField.toUpperCase(),
      resolvedField.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, ''),
      resolvedField.replace(/([A-Z])/g, '_$1').toUpperCase().replace(/^_/, ''),
      `Total_${resolvedField}`,
      `total_${resolvedField}`,
      `total${resolvedField}`,
      `TOTAL_${resolvedField}`
    ];

    for (const key of variants) {
      if (record[key] !== undefined && record[key] !== null) {
        let value = record[key];

        if (typeof value === 'object') {
          if ('value' in value) {
            value = value.value;
          } else if ('Value' in value) {
            value = value.Value;
          } else {
            continue;
          }
        }

        if (typeof value === 'string') {
          value = value.replace(/[,%]/g, '');
        }

        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? 0 : numericValue;
      }
    }

    return 0;
  };

  // Define metrics
  const metrics = [
    { 
      id: 'excessAwareness', 
      name: 'Excess Awareness',
      countField: 'TotalExcessAwareness',
      baseField: 'TotalAware',
      color: '#f44336',
      goodDirection: 'low' // lower is better
    },
    { 
      id: 'outstandingInterest', 
      name: 'Outstanding Interest',
      countField: 'TotalOutstandingInterest',
      baseField: 'TotalInterest',
      baseFallback: 'TotalAware',
      color: '#ffc107',
      goodDirection: 'high' // higher is better
    },
    { 
      id: 'awareOpeningWeekend', 
      name: 'Aware Opening Weekend',
      countField: 'TotalAwareOpeningWeekend',
      baseField: 'TotalAware',
      color: '#9c27b0',
      goodDirection: 'high'
    },
    { 
      id: 'outstandingOpeningWeekend', 
      name: 'Outstanding Opening Weekend',
      countField: 'TotalOutstandingOpeningWeekend',
      baseField: 'TotalResponses',
      color: '#e91e63',
      goodDirection: 'high'
    },
    { 
      id: 'theaterAmongAware', 
      name: 'Theater Among Aware',
      countField: 'TotalTheaterAmongAware',
      baseField: 'TotalAware',
      color: '#2196f3',
      goodDirection: 'high'
    },
    { 
      id: 'outstandingTheater', 
      name: 'Outstanding Theater',
      countField: 'TotalOutstandingTheater',
      baseField: 'TotalResponses',
      baseFallback: 'TotalAware',
      color: '#00bcd4',
      goodDirection: 'high'
    },
    { 
      id: 'intentConversion', 
      name: 'Intent Conversion',
      countField: 'TotalIntentConversion',
      baseField: 'TotalAware',
      color: '#4caf50',
      goodDirection: 'high'
    }
  ];

  // Build demographic segments with calculations
  const segments = data
    .filter((record) => record)
    .map(record => {
    const ethnicity = record.ethnicity || 'Unknown';
    const age = record.agegroup || 'Unknown';
    const gender = record.gender || 'Unknown';
    
      const segment = {
      name: `${ethnicity} / ${age} / ${gender}`,
      ethnicity,
      age,
      gender,
      totalResponses: getFieldValue(record, 'TotalResponses'),
      totalAware: getFieldValue(record, 'TotalAware'),
      totalInterest: getFieldValue(record, 'TotalInterest')
    };
    
    // Calculate each metric
    metrics.forEach(metric => {
      const count = getFieldValue(record, metric.countField);
      const base = getFieldValue(record, metric.baseField);

      const adjustedBase = base > 0
        ? base
        : metric.baseFallback
        ? getFieldValue(record, metric.baseFallback)
        : base;

      segment[metric.id] =
        adjustedBase > 0 ? (count / adjustedBase) * 100 : 0;
    });
    
    return segment;
    })
    .filter(s => {
      const hasResponses = s.totalResponses > 0 || s.totalAware > 0 || s.totalInterest > 0;
      const hasMetricValue = metrics.some(metric => (s[metric.id] || 0) > 0);
      return hasResponses || hasMetricValue;
    });

  // Handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort data
  const sortedSegments = [...segments].sort((a, b) => {
    const aValue = a[orderBy] || 0;
    const bValue = b[orderBy] || 0;
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Get color intensity based on value and metric
  const getColorIntensity = (value, metric) => {
    if (value === 0) return '#f5f5f5';
    
    const metricInfo = metrics.find(m => m.id === metric);
    if (!metricInfo) return '#f5f5f5';
    
    // Normalize to 0-1 scale
    const normalized = value / 100;
    
    // For 'low is good' metrics, invert
    const intensity = metricInfo.goodDirection === 'low' ? (1 - normalized) : normalized;
    
    // Calculate opacity (20% to 100%)
    const opacity = Math.round(20 + (intensity * 80));
    
    return `${metricInfo.color}${opacity.toString(16).padStart(2, '0')}`;
  };

  // Get performance chip
  const getPerformanceChip = (value, metric) => {
    const metricInfo = metrics.find(m => m.id === metric);
    if (!metricInfo) return null;
    
    let label = '';
    let color = '';
    
    if (metricInfo.goodDirection === 'high') {
      if (value >= 60) { label = 'Excellent'; color = brandColors.success; }
      else if (value >= 40) { label = 'Good'; color = brandColors.info; }
      else if (value >= 20) { label = 'Fair'; color = brandColors.warning; }
      else { label = 'Low'; color = brandColors.error; }
    } else {
      if (value < 20) { label = 'Excellent'; color = brandColors.success; }
      else if (value < 40) { label = 'Good'; color = brandColors.info; }
      else if (value < 60) { label = 'Fair'; color = brandColors.warning; }
      else { label = 'High'; color = brandColors.error; }
    }
    
    return (
      <Chip 
        label={label} 
        size="small" 
        sx={{ 
          backgroundColor: `${color}20`, 
          color, 
          fontWeight: 600,
          fontSize: '0.7rem'
        }} 
      />
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Demographic Performance Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sortable grid showing all metrics across demographic segments
        </Typography>
      </Box>

      {/* Metric Selector */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Highlight Metric</InputLabel>
            <Select
              value={selectedMetric}
              label="Highlight Metric"
              onChange={(e) => setSelectedMetric(e.target.value)}
            >
              <MenuItem value="all">All Metrics</MenuItem>
              {metrics.map(metric => (
                <MenuItem key={metric.id} value={metric.id}>{metric.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Data Table */}
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Demographic Segment
                  {orderBy === 'name' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              
              <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>
                <TableSortLabel
                  active={orderBy === 'totalResponses'}
                  direction={orderBy === 'totalResponses' ? order : 'asc'}
                  onClick={() => handleRequestSort('totalResponses')}
                >
                  Sample
                  {orderBy === 'totalResponses' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              {metrics.map(metric => (
                <TableCell 
                  key={metric.id} 
                  align="right" 
                  sx={{ 
                    fontWeight: 700, 
                    backgroundColor: selectedMetric === metric.id ? `${metric.color}20` : '#f5f5f5',
                    minWidth: 120
                  }}
                >
                  <TableSortLabel
                    active={orderBy === metric.id}
                    direction={orderBy === metric.id ? order : 'asc'}
                    onClick={() => handleRequestSort(metric.id)}
                  >
                    {metric.name}
                    {orderBy === metric.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSegments.map((segment, index) => (
              <TableRow key={index} hover>
                <TableCell sx={{ fontWeight: 600 }}>
                  {segment.name}
                </TableCell>
                
                <TableCell align="right">
                  {segment.totalResponses.toLocaleString()}
                </TableCell>

                {metrics.map(metric => (
                  <TableCell 
                    key={metric.id} 
                    align="right"
                    sx={{ 
                      backgroundColor: selectedMetric === 'all' || selectedMetric === metric.id 
                        ? getColorIntensity(segment[metric.id], metric.id)
                        : 'transparent'
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {segment[metric.id].toFixed(1)}%
                      </Typography>
                      {selectedMetric === metric.id && getPerformanceChip(segment[metric.id], metric.id)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Color Intensity Guide
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Darker colors indicate stronger performance. For "Excess Awareness", lower values are better (less waste). 
          For all other metrics, higher values indicate better performance.
        </Typography>
      </Box>
    </Paper>
  );
};

export default DemographicPerformanceDashboard;

